import { useEffect, useRef, useState } from 'react';
import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import './App.css';
import Home from './Home';
import About from './About';
import RootsProjectDetail from './projects/RootsProjectDetail';
import DishaProjectDetail from './projects/DishaProjectDetail';
import HabitatProjectDetail from './projects/HabitatProjectDetail';
import HindsightProjectDetail from './projects/HindsightProjectDetail';
// import HardhatProjectDetail from './projects/HardhatProjectDetail';
import NotFound from './NotFound';

interface NavbarProps {
  scrolled: boolean;
}

function Navbar({ scrolled }: NavbarProps) {
  return (
    <nav className={`navbar${scrolled ? ' scrolled' : ''}`}>
      <div className="navbar-left">
        <img src="/logo.webp" alt="Logo" className="navbar-logo navbar-logo-large" />
        <NavLink to="/" className="title">Luke Fiorante</NavLink>
      </div>
      <div className="navbar-right">
        <NavLink to="/" end className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}>Projects</NavLink>
        <NavLink to="/about" className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}>About</NavLink>
        <a
          href="https://www.linkedin.com/in/fiorante"
          target="_blank"
          rel="noopener noreferrer"
          className="nav-link-icon"
          aria-label="LinkedIn"
        >
          <img src="/linkedin.webp" alt="LinkedIn" className="navbar-linkedin-icon" />
        </a>
      </div>
    </nav>
  );
}

const pageVariants = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.36, ease: [0.4, 0.2, 0.2, 1] } },
  exit: { opacity: 0, y: -24, transition: { duration: 0.22, ease: [0.4, 0.2, 0.2, 1] } },
};

// Helper function to preload images
const preloadImages = (imageUrls: string[]) => {
  imageUrls.forEach((url: string) => {
    const img = new Image();
    img.src = url;
  });
};

function App() {
  const [scrolled, setScrolled] = useState(false);
  const mainRef = useRef(null);
  const location = useLocation();

  // Preload critical images when app loads
  useEffect(() => {
    const criticalImages = [
      // About page images
      '/about/headshot.webp',
      '/about/harvard.webp',
      '/about/brown.webp',
      '/about/nasa.webp',
      '/about/waymo.webp',
      '/about/google.webp',
      // Navbar images
      '/logo.webp',
      '/linkedin.webp',
      // Project hero images
      '/hindsight/hero.webp',
      '/disha/hero.webp',
      '/habitat2100/hero.webp',
      '/rooted_in_place/hero.webp'
    ];
    preloadImages(criticalImages);
  }, []);

  useEffect(() => {
    function onScroll() {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 40);
    }
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="App">
      <Navbar scrolled={scrolled} />
      <div className="main-content" ref={mainRef}>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ minHeight: '100%' }}
          >
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/project/disha" element={<DishaProjectDetail />} />
              <Route path="/project/habitat2100" element={<HabitatProjectDetail />} />
              <Route path="/project/hindsight" element={<HindsightProjectDetail />} />
              {/* <Route path="/project/heart_hat" element={<HardhatProjectDetail />} /> */}
              <Route path="/project/rooted_in_place" element={<RootsProjectDetail />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default App;
